import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Gift, Receipt, UserRound } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { usePassbook } from '@/shared/lib/passbook';
import { useAuth } from '@/shared/lib/auth';
import { PageHeader } from '@/shared/components/page-header';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import { cn } from '@/shared/lib/cn';
import { previewOperatorBill } from '@/shared/lib/loyalty-engine';
import { passbookCurrency, quantityLabel } from '@/shared/lib/passbook-utils';
import type { Lead, PointLog } from '@/shared/types';
import { useOperatorSearch } from '../api/operator-search';
import { useOperatorPreview } from '../api/operator-preview';
import type { OperatorPreviewResult } from '../api/operator-preview';
import { useAwardableProducts, useCatalogProducts } from '../api/operator-catalog';
import {
  useOperatorAwardRetry,
  useOperatorCombo,
  useOperatorRecentActivity,
} from '../api/operator-combo';
import { purchaseValueFieldMeta } from '../model/operator-add-fields';
import {
  blocksEarningForRole,
  buildBenefitLines,
  detectOperatorProgram,
  OPERATOR_REDEMPTION_ONLY_PURCHASE,
  requiresPurchaseValue,
  resolvePreviewChannel,
  showsRedemptionCatalog,
  usesManualStampCount,
  usesOptionalProductDetails,
  usesProductDetailsInput,
  usesPurchaseValueInput,
} from '../model/operator-rules';
import type { Product } from '@/shared/types/product';
import {
  USER_BLOCKED_MESSAGE,
  DUPLICATE_COUPON_MESSAGE,
  isDuplicateCouponError,
  isLeadBlockedError,
  operatorErrorMessage,
} from '../model/operator-errors';
import { getAddFieldErrors } from '../model/operator-add-fields';
import { newOperatorExternalId, sanitizePurchaseCode } from '../model/operator-external-id';
import type { CatalogProduct, OperatorConfirmPayload, OperatorSaleDetail } from '../model/operator-payload';
import { catalogBillDiscount, catalogCost } from '../model/operator-payload';
import type { QrParseResult } from '../model/nfce-qr';
import { OperatorSearchBar, type OperatorSearchStatus } from './operator-search-bar';
import { OperatorCustomerCard, OperatorIdentifyEmpty } from './operator-customer-card';
import { OperatorAccountStep } from './operator-account-step';
import { OperatorBenefitsStep } from './operator-benefits-step';
import { OperatorSummaryCard } from './operator-summary-card';
import { OperatorConfirmSheet } from './operator-confirm-sheet';
import { OperatorQrScanner } from './operator-qr-scanner';
import {
  OperatorSuccessOverlay,
  type OperatorSuccessReward,
} from './operator-success-overlay';
import { OperatorReceipt, type OperatorReceiptData } from './operator-receipt';
import { extractReceiptBalance } from './operator-receipt-balance';
import { formatMaskedMoney, moneyFromNumber, parseMaskedMoney } from '../model/operator-money';
import { getRedemptionExcessError } from '../model/operator-redemption-validation';

/** Transição leve entre identificar ↔ venda. */
const VIEW_EASE = [0.22, 1, 0.36, 1] as const;
const viewTransition = { duration: 0.34, ease: VIEW_EASE };

function OperatorViewFrame({
  viewKey,
  children,
}: {
  viewKey: string;
  children: ReactNode;
}) {
  return (
    <motion.div
      key={viewKey}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={viewTransition}
      className="flex flex-1 flex-col lg:min-h-0"
    >
      {children}
    </motion.div>
  );
}

/** Referências estáveis — `data ?? []` cria array novo a cada render e explode useMemo/useEffect. */
const EMPTY_LOGS: PointLog[] = [];
const EMPTY_PRODUCTS: CatalogProduct[] = [];
const EMPTY_STORE_PRODUCTS: Product[] = [];
const EMPTY_SALE_DETAILS: OperatorSaleDetail[] = [];

function benefitStateEqual(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => a[k] === b[k]);
}

/** resgateNaoPontua / verifyLogDate: zera ganho sem mexer em compra/desconto/resgate. */
function applyEarningBlock(
  preview: OperatorPreviewResult,
  blocked: boolean,
  message = 'Sem crédito de fidelidade para este operador',
): OperatorPreviewResult {
  if (!blocked || preview.awardUnits <= 0) return preview;
  return {
    ...preview,
    earnBase: 0,
    earnBaseMode: 'none',
    pointsEarned: 0,
    cashbackEarned: 0,
    stampsEarned: 0,
    awardUnits: 0,
    message,
  };
}

export function OperatorPage() {
  const { idPassbook, passbook } = usePassbook();
  const { user } = useAuth();
  const program = detectOperatorProgram(passbook);
  const currencyCode = passbookCurrency(passbook);
  const earningBlocked = blocksEarningForRole(passbook, user?.role);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<Lead[]>([]);
  const [searchStatus, setSearchStatus] = useState<OperatorSearchStatus>('idle');
  const [selected, setSelected] = useState<Lead | null>(null);
  const leadBlocked = selected != null && selected.status === 'blocked';

  const [purchaseValue, setPurchaseValue] = useState('');
  const [redemptionOnly, setRedemptionOnly] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [purchaseCode, setPurchaseCode] = useState('');
  const [addFieldValues, setAddFieldValues] = useState<Record<string, string>>({});
  const [stampCount, setStampCount] = useState(1);
  const [saleDetails, setSaleDetails] = useState<OperatorSaleDetail[]>(EMPTY_SALE_DETAILS);
  const [benefitState, setBenefitState] = useState<Record<string, boolean>>({});
  const [redemptionAmount, setRedemptionAmount] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<CatalogProduct | null>(null);
  const [preview, setPreview] = useState<OperatorPreviewResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successReward, setSuccessReward] = useState<OperatorSuccessReward | null>(null);
  const [partialError, setPartialError] = useState<string | null>(null);
  const [partialRedemptionRan, setPartialRedemptionRan] = useState(false);
  const [lastPayload, setLastPayload] = useState<OperatorConfirmPayload | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<OperatorReceiptData | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [nfceKey, setNfceKey] = useState<string | undefined>();
  const [nfceUrl, setNfceUrl] = useState<string | undefined>();
  const [wizardIndex, setWizardIndex] = useState(0);
  const confirmLock = useRef(false);
  const searchSeq = useRef(0);
  const previewSeq = useRef(0);
  /** Após QR de cliente: se a busca retornar exatamente 1 lead, seleciona direto. */
  const autoSelectSingleFromQr = useRef(false);

  const search = useOperatorSearch(idPassbook, passbook);
  const previewMutation = useOperatorPreview(idPassbook, passbook);
  const combo = useOperatorCombo(idPassbook, passbook);
  const awardRetry = useOperatorAwardRetry(idPassbook, passbook);
  const { data: recentLogsData, isFetching: logsLoading, refetch: refetchLogs } = useOperatorRecentActivity(
    idPassbook,
    selected?.serialNumber,
  );
  const recentLogs = recentLogsData ?? EMPTY_LOGS;

  const balance = selected?.total_points ?? 0;
  const redemptionCatalogEnabled = Boolean(selected && showsRedemptionCatalog(passbook));
  const needsProductDetails = usesProductDetailsInput(passbook);
  const optionalProductDetails = usesOptionalProductDetails(passbook);
  const showProductPicker = needsProductDetails || optionalProductDetails;
  const hasSaleLines = saleDetails.some((d) => d.count > 0);
  const parsedPurchase = parseMaskedMoney(purchaseValue);
  /** Valor efetivo nas APIs/prévia: só-resgate → R$ 0,01. */
  const effectivePurchase = redemptionOnly ? OPERATOR_REDEMPTION_ONLY_PURCHASE : parsedPurchase;

  const {
    data: catalogProductsData,
    isPending: catalogPending,
    isError: catalogError,
  } = useCatalogProducts(
    idPassbook,
    Math.floor(balance),
    redemptionCatalogEnabled,
    program,
    passbook.redeemType,
    passbook.usesProductValueFieldAsDiscount,
    passbook.maxInputValidation,
  );
  const catalogProducts = catalogProductsData ?? EMPTY_PRODUCTS;

  const { data: storeProductsData, isFetching: storeProductsLoading } = useAwardableProducts(
    idPassbook,
    Boolean(selected && showProductPicker),
  );
  const storeProducts = storeProductsData ?? EMPTY_STORE_PRODUCTS;

  const onSaleDetailChange = useCallback((sku: string, name: string, count: number) => {
    setSaleDetails((prev) => {
      const next = prev.filter((d) => d.sku !== sku);
      if (count > 0) next.push({ sku, name, count });
      return next.length === 0 ? EMPTY_SALE_DETAILS : next;
    });
  }, []);

  const onAddFieldChange = useCallback((slug: string, value: string) => {
    setAddFieldValues((prev) => ({ ...prev, [slug]: value }));
  }, []);

  const redeemedToday = useMemo(
    () => recentLogs.some((l) => l.action === 'resgate' && isToday(l.date)),
    [recentLogs],
  );

  /** verifyLogDate: já houve lançamento (selo/pontos) hoje para este CPF. */
  const validatedToday = useMemo(() => {
    if (!passbook.verifyLogDate) return false;
    return recentLogs.some(
      (l) =>
        (l.action === 'input' || l.action === 'addPoints') &&
        isToday(l.date) &&
        ((l.pointsChanged ?? 0) > 0 || l.value > 0),
    );
  }, [passbook.verifyLogDate, recentLogs]);

  const benefitLines = useMemo(() => {
    if (!selected) return [];
    const lines = buildBenefitLines({
      passbook,
      balance,
      purchaseValue: parsedPurchase,
      redeemedToday,
    });
    // Mantém a linha enquanto carrega (evita “sumiu” por race); só esconde se
    // a query terminou vazia (erro → ainda mostra pra o empty state avisar).
    if (!catalogPending && catalogProducts.length === 0 && !catalogError) {
      return lines.filter((l) => l.id !== 'catalog');
    }
    return lines;
  }, [
    selected,
    passbook,
    balance,
    parsedPurchase,
    redeemedToday,
    catalogProducts.length,
    catalogPending,
    catalogError,
  ]);

  const addFieldErrors = useMemo(
    () =>
      getAddFieldErrors(passbook, {
        ...addFieldValues,
        numberCodCupom: purchaseCode || addFieldValues.numberCodCupom,
      }),
    [passbook, addFieldValues, purchaseCode],
  );

  const hasAddFieldErrors = Object.keys(addFieldErrors).length > 0;

  // Inicializa toggles / clamp do valor quando linhas ou teto mudam.
  useEffect(() => {
    setBenefitState((prev) => {
      const next: Record<string, boolean> = {};
      for (const line of benefitLines) {
        const was = prev[line.id] ?? line.defaultOn;
        // Inelegível → desliga (evita confirmar resgate morto).
        next[line.id] = line.eligible ? was : false;
      }
      return benefitStateEqual(prev, next) ? prev : next;
    });
    const catalogLine = benefitLines.find((l) => l.id === 'catalog');
    if (catalogLine && !catalogLine.eligible) {
      setSelectedProduct(null);
    }
    // Autofill sempre no limite elegível (compra/saldo mudam → reaplicar teto).
    const balanceLine = benefitLines.find((l) => l.id === 'balance' && l.eligible && l.maxAmount != null);
    if (balanceLine?.maxAmount != null && (balanceLine.kind === 'cash' || balanceLine.kind === 'points')) {
      const max = balanceLine.maxAmount;
      setRedemptionAmount((prev) => {
        if (balanceLine.kind === 'cash') {
          const next = formatMaskedMoney(max);
          return prev === next ? prev : next;
        }
        const floorMax = Math.floor(max);
        const next = String(floorMax);
        return prev === next ? prev : next;
      });
    }
  }, [benefitLines, selected?._id]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    // Cliente já escolhido: não rebusca (ex.: selectLead preenche a query com CPF/nome).
    if (selected) {
      searchSeq.current += 1;
      return;
    }
    if (debounced.length < 2) {
      setResults([]);
      setSearchStatus('idle');
      return;
    }
    const seq = ++searchSeq.current;
    setSearchStatus('loading');
    search.mutate(debounced, {
      onSuccess: (data) => {
        if (seq !== searchSeq.current) return;
        const fromQr = autoSelectSingleFromQr.current;
        autoSelectSingleFromQr.current = false;
        if (fromQr && data.length === 1 && data[0]) {
          selectLeadRef.current(data[0]);
          return;
        }
        setResults(data);
        setSearchStatus(data.length === 0 ? 'empty' : 'found');
      },
      onError: () => {
        if (seq !== searchSeq.current) return;
        autoSelectSingleFromQr.current = false;
        setResults([]);
        setSearchStatus('idle');
        toast.error('Falha na busca.');
      },
    });
  }, [debounced, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const redemptionOn = Boolean(
    (benefitState.balance && benefitLines.some((l) => l.id === 'balance' && l.eligible)) ||
      (benefitState.catalog &&
        selectedProduct &&
        benefitLines.some((l) => l.id === 'catalog' && l.eligible)),
  );

  /** Débito no saldo fidelidade (pts ou R$ conforme o programa) — corpo do resgate. */
  const redemptionValue = useMemo(() => {
    if (benefitState.catalog && selectedProduct) {
      return catalogCost(selectedProduct, passbook);
    }
    if (benefitState.balance) {
      const line = benefitLines.find((l) => l.id === 'balance');
      if (line?.kind === 'points') return Number(redemptionAmount) || 0;
      if (line?.kind === 'stamps') return line.maxAmount ?? 0;
      return parseMaskedMoney(redemptionAmount);
    }
    return 0;
  }, [benefitState, selectedProduct, redemptionAmount, benefitLines, passbook]);

  /**
   * Desconto em R$ na conta.
   * Cashback/saldo → valor digitado. Catálogo → só se redemptionType=discount (price).
   * Brinde/produto NÃO vira desconto em R$ (custo em pts ≠ reais).
   */
  const billDiscount = useMemo(() => {
    if (benefitState.catalog && selectedProduct) {
      return catalogBillDiscount(selectedProduct, passbook);
    }
    if (benefitState.balance) {
      const line = benefitLines.find((l) => l.id === 'balance');
      if (line?.kind === 'cash') return parseMaskedMoney(redemptionAmount);
    }
    return 0;
  }, [benefitState, selectedProduct, redemptionAmount, benefitLines, passbook]);

  /** true quando a prévia precisa receber billDiscount explícito (inclui 0 no brinde). */
  const passBillDiscount = Boolean(
    (benefitState.catalog && selectedProduct) ||
      (benefitState.balance && benefitLines.some((l) => l.id === 'balance' && l.kind === 'cash')),
  );

  const toggleBenefit = useCallback((id: string, on: boolean) => {
    setBenefitState((s) => {
      const next = { ...s, [id]: on };
      // Cashback/saldo e catálogo são mutuamente exclusivos (um resgate por conta).
      if (on && id === 'catalog') next.balance = false;
      if (on && id === 'balance') {
        next.catalog = false;
      }
      return next;
    });
    if (on && id === 'balance') setSelectedProduct(null);
  }, []);

  const selectCatalogProduct = useCallback((p: CatalogProduct | null) => {
    setSelectedProduct(p);
    if (p) {
      setBenefitState((s) => ({ ...s, catalog: true, balance: false }));
    }
  }, []);

  const selectedUniqueId = selected?.profile?.uniqueId;

  useEffect(() => {
    if (successOpen) {
      previewSeq.current += 1;
      return;
    }
    // Prévia só precisa do cliente selecionado (serial é exigido só no lançamento).
    if (!selected) {
      setPreview(null);
      setPreviewError(null);
      return;
    }

    if (leadBlocked) {
      previewSeq.current += 1;
      setPreview(null);
      setPreviewError(USER_BLOCKED_MESSAGE);
      toast.error(USER_BLOCKED_MESSAGE, { id: 'operator-user-blocked' });
      return;
    }

    const localRedemptionPreview = (purchase: number, discount: number): OperatorPreviewResult => ({
      program: detectOperatorProgram(passbook),
      purchaseValue: purchase,
      discount,
      netValue: Math.max(0, purchase - discount),
      earnBase: 0,
      earnBaseMode: 'none',
      pointsEarned: 0,
      cashbackEarned: 0,
      stampsEarned: 0,
      awardUnits: 0,
      message: 'Somente resgate — sem lançamento nesta conta',
      source: 'local',
    });

    // Conta só-resgate: não chama simulate; APIs usam R$ 0,01 e award = 0.
    if (redemptionOnly) {
      // Invalida simulate em voo (senão onSuccess sobrescreve a prévia local).
      previewSeq.current += 1;
      setPreviewError(null);
      setPreview(localRedemptionPreview(OPERATOR_REDEMPTION_ONLY_PURCHASE, 0));
      return;
    }

    if (needsProductDetails && !hasSaleLines) {
      if (redemptionOn) {
        setPreviewError(null);
        const discount = passBillDiscount ? billDiscount : program === 'points' || program === 'stamps' ? 0 : redemptionValue;
        setPreview(localRedemptionPreview(0, discount));
      } else {
        setPreview((p) => (p === null ? p : null));
        setPreviewError(null);
      }
      return;
    }

    const purchase = usesPurchaseValueInput(passbook)
      ? effectivePurchase
      : usesManualStampCount(passbook)
        ? stampCount
        : effectivePurchase;

    if ((requiresPurchaseValue(passbook) || earningBlocked) && purchase <= 0) {
      if (redemptionOn && !earningBlocked) {
        setPreviewError(null);
        const discount = passBillDiscount ? billDiscount : program === 'points' || program === 'stamps' ? 0 : redemptionValue;
        setPreview(localRedemptionPreview(0, discount));
      } else {
        setPreview((p) => (p === null ? p : null));
        setPreviewError(null);
      }
      return;
    }

    // Giftcard/saldo: pode prévia só com resgate (sem valor de compra).
    if (
      !requiresPurchaseValue(passbook) &&
      !earningBlocked &&
      usesPurchaseValueInput(passbook) &&
      purchase <= 0 &&
      !redemptionOn
    ) {
      setPreview((p) => (p === null ? p : null));
      setPreviewError(null);
      return;
    }

    if (usesManualStampCount(passbook)) {
      setPreviewError(null);
      const purchaseAmt = usesPurchaseValueInput(passbook) ? effectivePurchase : 0;
      const discount = passBillDiscount ? billDiscount : 0;
      setPreview(
        applyEarningBlock(
          {
            program: 'stamps',
            purchaseValue: purchaseAmt,
            discount,
            netValue: Math.max(0, purchaseAmt - discount),
            earnBase: 0,
            earnBaseMode: 'none',
            pointsEarned: 0,
            cashbackEarned: 0,
            stampsEarned: stampCount,
            awardUnits: stampCount,
            message: `Cliente ganha: ${stampCount} selos`,
            source: 'local',
          },
          earningBlocked,
        ),
      );
      return;
    }

    const seq = ++previewSeq.current;
    const detailsPayload = showProductPicker ? saleDetails.filter((d) => d.count > 0) : undefined;
    const t = setTimeout(() => {
      previewMutation.mutate(
        {
          uniqueId: selectedUniqueId,
          purchaseValue: purchase,
          redemptionAmount: redemptionOn ? redemptionValue : 0,
          billDiscount: passBillDiscount ? billDiscount : undefined,
          productRedemption: Boolean(benefitState.catalog && selectedProduct),
          balance,
          ...(detailsPayload && detailsPayload.length > 0 ? { details: detailsPayload } : {}),
        },
        {
          onSuccess: (data) => {
            if (seq !== previewSeq.current) return;
            setPreviewError(null);
            setPreview(
              applyEarningBlock(
                data,
                earningBlocked || validatedToday,
                validatedToday
                  ? 'Validação já efetuada hoje!'
                  : 'Sem crédito de fidelidade para este operador',
              ),
            );
          },
          onError: (err) => {
            if (seq !== previewSeq.current) return;
            setPreview(null);
            if (isLeadBlockedError(err)) {
              setPreviewError(USER_BLOCKED_MESSAGE);
              toast.error(USER_BLOCKED_MESSAGE, { id: 'operator-user-blocked' });
              return;
            }
            setPreviewError('Operação fora da regra');
            toast.error('Operação fora da regra');
          },
        },
      );
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selected,
    selectedUniqueId,
    leadBlocked,
    purchaseValue,
    parsedPurchase,
    effectivePurchase,
    redemptionOnly,
    stampCount,
    saleDetails,
    needsProductDetails,
    showProductPicker,
    hasSaleLines,
    redemptionOn,
    redemptionValue,
    billDiscount,
    passBillDiscount,
    benefitState.catalog,
    selectedProduct,
    passbook,
    program,
    successOpen,
    balance,
    earningBlocked,
    validatedToday,
  ]);

  const resetBillFields = useCallback(() => {
    setPurchaseValue('');
    setRedemptionOnly(false);
    setPreviewError(null);
    setPurchaseCode('');
    setAddFieldValues({});
    setStampCount(1);
    setSaleDetails(EMPTY_SALE_DETAILS);
    setSelectedProduct(null);
    setPreview(null);
    setPartialError(null);
    setPartialRedemptionRan(false);
    setConfirmOpen(false);
    setLastPayload(null);
    setNfceKey(undefined);
    setNfceUrl(undefined);
    setBenefitState({});
    setRedemptionAmount('');
    setWizardIndex(0);
  }, []);

  const selectLead = useCallback((lead: Lead) => {
    setSelected(lead);
    setResults([]);
    setSearchStatus('idle');
    setQuery(lead.profile?.uniqueId ?? lead.profile?.name ?? '');
    resetBillFields();
    if (lead.status === 'blocked') {
      toast.error(USER_BLOCKED_MESSAGE, { id: 'operator-user-blocked' });
    }
  }, [resetBillFields]);

  const selectLeadRef = useRef(selectLead);
  selectLeadRef.current = selectLead;

  const clearCustomer = useCallback(() => {
    setSelected(null);
    setQuery('');
    setDebounced('');
    setResults([]);
    setSearchStatus('idle');
    autoSelectSingleFromQr.current = false;
    resetBillFields();
  }, [resetBillFields]);

  const refresh = useCallback(() => {
    refetchLogs();
  }, [refetchLogs]);

  const handleQr = useCallback(
    (result: QrParseResult) => {
      if (result.kind === 'customer') {
        autoSelectSingleFromQr.current = true;
        setQuery(result.uniqueId);
        setDebounced(result.uniqueId);
        return;
      }
      if (result.kind === 'nfce') {
        if (result.totalValue != null) setPurchaseValue(moneyFromNumber(result.totalValue));
        setNfceKey(result.accessKey);
        setNfceUrl(result.rawUrl);
        if (result.totalValue == null) {
          toast.message('Nota lida', {
            description: 'Valor não estava no QR — preencha manualmente.',
          });
        }
        return;
      }
      toast.error('QR não reconhecido.');
    },
    [],
  );

  const buildPayload = useCallback((): OperatorConfirmPayload | null => {
    if (!selected?.serialNumber || !selected.profile?.uniqueId) return null;
    const purchase = usesPurchaseValueInput(passbook)
      ? effectivePurchase
      : redemptionOnly
        ? OPERATOR_REDEMPTION_ONLY_PURCHASE
        : 0;
    const sanitizedCode =
      sanitizePurchaseCode(purchaseCode) ||
      sanitizePurchaseCode(addFieldValues.numberCodCupom ?? '');
    // Idempotência: sempre op- único. Cupom/NFC-e só em addFields (como dash antigo).
    const externalId = newOperatorExternalId();
    const details =
      !redemptionOnly && showProductPicker ? saleDetails.filter((d) => d.count > 0) : undefined;

    // Só-resgate / role bloqueada / já validou hoje (verifyLogDate): nunca lança fidelidade.
    // Cashback/pontos: SEMPRE recalcula no confirm (preview debounce/stale
    // com resgate defaultOn zerava awardUnits → noop sem addPoints em prod).
    let award = 0;
    let earnBase = redemptionOnly ? 0 : (preview?.earnBase ?? purchase);
    let net = redemptionOnly
      ? OPERATOR_REDEMPTION_ONLY_PURCHASE
      : (preview?.netValue ?? purchase);

    if (redemptionOnly || earningBlocked || validatedToday) {
      award = 0;
    } else if (usesManualStampCount(passbook)) {
      award = stampCount;
    } else if (resolvePreviewChannel(passbook) === 'bff' && purchase > 0) {
      const bill = previewOperatorBill({
        passbook,
        purchaseValue: purchase,
        redemptionAmount: redemptionOn ? redemptionValue : 0,
        billDiscount: passBillDiscount ? billDiscount : undefined,
        productRedemption: Boolean(benefitState.catalog && selectedProduct),
        balance,
      });
      award = bill.awardUnits;
      earnBase = bill.earnBase;
      net = bill.netValue;
    } else {
      award = preview?.awardUnits ?? 0;
    }

    return {
      idPassbook,
      uniqueId: selected.profile.uniqueId,
      serialNumber: selected.serialNumber,
      userReceive: selected.profile.name ?? '',
      purchaseValue: purchase,
      awardUnits: award,
      earnBase,
      netValue: net,
      redemptionEnabled: redemptionOn,
      redemptionAmount: redemptionValue,
      productRedemption: benefitState.catalog ? selectedProduct : null,
      externalId,
      purchaseCode: sanitizedCode || undefined,
      nfceAccessKey: nfceKey,
      nfceRawUrl: nfceUrl,
      details: details && details.length > 0 ? details : undefined,
      addFieldValues: Object.keys(addFieldValues).length > 0 ? addFieldValues : undefined,
    };
  }, [
    selected,
    passbook,
    effectivePurchase,
    redemptionOnly,
    earningBlocked,
    validatedToday,
    preview,
    stampCount,
    idPassbook,
    redemptionOn,
    redemptionValue,
    benefitState,
    selectedProduct,
    purchaseCode,
    addFieldValues,
    nfceKey,
    nfceUrl,
    showProductPicker,
    saleDetails,
    passBillDiscount,
    billDiscount,
    balance,
  ]);

  const redemptionError = useMemo(
    () =>
      selected
        ? getRedemptionExcessError({
            benefitLines,
            benefitState,
            redemptionAmount,
            redemptionValue,
            selectedProduct,
            balance,
            currencyCode,
            program,
            usesValueAsDiscount: passbook.usesProductValueFieldAsDiscount,
            maxInputValidation: passbook.maxInputValidation,
          })
        : null,
    [
      selected,
      benefitLines,
      benefitState,
      redemptionAmount,
      redemptionValue,
      selectedProduct,
      balance,
      currencyCode,
      program,
      passbook,
    ],
  );
  const redemptionExceeded = Boolean(redemptionError);

  const finishSuccessSequence = useCallback(() => {
    setSuccessOpen(false);
    setSuccessReward(null);
    clearCustomer();
  }, [clearCustomer]);

  const finishReceiptSequence = useCallback(() => {
    setReceiptOpen(false);
    setReceiptData(null);
    clearCustomer();
  }, [clearCustomer]);

  const applyPostConfirmSuccess = useCallback(
    (
      payload: OperatorConfirmPayload,
      result: { redemptionData?: unknown; redemptionRan?: boolean; awardRan?: boolean },
    ) => {
      const isMoneyRedemption =
        payload.redemptionEnabled && (program === 'giftcard' || program === 'balance');

      if (isMoneyRedemption) {
        const newBalance = extractReceiptBalance(
          result.redemptionData,
          Math.max(0, balance - payload.redemptionAmount),
        );
        setSelected((prev) => (prev ? { ...prev, total_points: newBalance } : prev));
        setReceiptData({
          customerName: payload.userReceive,
          uniqueId: payload.uniqueId,
          organizationName: passbook.organizationName,
          purchaseCode: payload.purchaseCode,
          creditUsed: payload.redemptionAmount,
          balance: newBalance,
          date: new Date(),
        });
        setConfirmOpen(false);
        setReceiptOpen(true);
        refresh();
        return;
      }

      // Só celebra ganho se o leg de lançamento rodou de fato (evita falso positivo).
      const awarded = Boolean(result.awardRan) && payload.awardUnits > 0;
      const awardUnits = awarded ? payload.awardUnits : 0;
      const reward: OperatorSuccessReward = {
        program: preview?.program ?? program,
        awardUnits,
        cashbackEarned: awarded && program === 'cashback' ? awardUnits : 0,
        pointsEarned: awarded && program === 'points' ? awardUnits : 0,
        stampsEarned: awarded && program === 'stamps' ? awardUnits : 0,
        quantityLabel: quantityLabel(passbook),
        redemptionProductName: payload.productRedemption?.name,
        redemptionProductCost: payload.productRedemption
          ? catalogCost(payload.productRedemption, passbook)
          : undefined,
      };

      setSelected((prev) => {
        if (!prev) return prev;
        let next = prev.total_points ?? 0;
        if (payload.redemptionEnabled) next -= payload.redemptionAmount;
        if (awarded && program !== 'giftcard' && program !== 'balance') {
          next += awardUnits;
        }
        return { ...prev, total_points: Math.max(0, next) };
      });

      setConfirmOpen(false);
      setSuccessReward(reward);
      setSuccessOpen(true);
    },
    [balance, passbook, preview, program, refresh],
  );

  const validateBeforeConfirm = useCallback((): boolean => {
    if (hasAddFieldErrors) {
      const first = Object.values(addFieldErrors)[0];
      toast.error(first ?? 'Preencha os campos obrigatórios.');
      return false;
    }
    return true;
  }, [hasAddFieldErrors, addFieldErrors]);

  const handleDuplicateCoupon = useCallback(() => {
    toast.error(DUPLICATE_COUPON_MESSAGE);
    setConfirmOpen(false);
    // Step "Conta" — valor da compra / código do cupom.
    setWizardIndex(1);
  }, []);

  const handleConfirm = async () => {
    if (confirmLock.current) return;
    if (leadBlocked) {
      toast.error(USER_BLOCKED_MESSAGE);
      setConfirmOpen(false);
      return;
    }
    if (previewError) {
      toast.error(previewError);
      return;
    }
    if (redemptionExceeded) {
      toast.error(redemptionError ?? 'Resgate acima do disponível.');
      return;
    }
    if (!validateBeforeConfirm()) return;

    const payload = buildPayload();
    if (!payload) {
      toast.error('Selecione um cliente.');
      return;
    }
    // verifyLogDate: 1 lançamento por CPF/dia — sem resgate na conta, bloqueia.
    if (validatedToday && !payload.redemptionEnabled && !redemptionOnly) {
      toast.error('Validação já efetuada hoje!');
      return;
    }
    if (benefitState.catalog && !selectedProduct) {
      toast.error('Selecione um produto do catálogo para resgatar.');
      return;
    }
    if (
      !redemptionOnly &&
      (requiresPurchaseValue(passbook) || earningBlocked) &&
      payload.purchaseValue <= 0 &&
      (earningBlocked || (payload.awardUnits <= 0 && !payload.redemptionEnabled))
    ) {
      toast.error('Informe o valor da compra.');
      return;
    }
    if (
      !redemptionOnly &&
      needsProductDetails &&
      !hasSaleLines &&
      payload.awardUnits <= 0 &&
      !payload.redemptionEnabled
    ) {
      toast.error('Selecione ao menos um produto.');
      return;
    }
    // Compra + cashback/pontos sem ganho e sem resgate → não confirmar no vazio.
    // Com resgate na mesma conta, award 0 pode ser líquido mínimo / resgateNaoPontua;
    // o recalc no buildPayload já usou o líquido — se award > 0, o combo lança a sobra.
    if (
      !redemptionOnly &&
      !earningBlocked &&
      resolvePreviewChannel(passbook) === 'bff' &&
      payload.purchaseValue > 0 &&
      payload.awardUnits <= 0 &&
      !payload.redemptionEnabled
    ) {
      toast.error('Não foi possível calcular o cashback/pontos desta compra. Verifique o valor.');
      return;
    }

    confirmLock.current = true;
    setLastPayload(payload);
    setPartialError(null);
    setPartialRedemptionRan(false);

    try {
      const result = await combo.mutateAsync(payload);
      if (result.phase === 'partial') {
        if (
          result.errorMessage === DUPLICATE_COUPON_MESSAGE ||
          result.errorMessage?.toLowerCase().includes('cupom já existente')
        ) {
          handleDuplicateCoupon();
          return;
        }
        if (result.redemptionRan) {
          setPartialError(result.errorMessage ?? 'Falha no lançamento.');
          setPartialRedemptionRan(true);
        } else {
          toast.error(result.errorMessage ?? operatorErrorMessage(new Error('Falha na operação.')));
        }
        return;
      }

      applyPostConfirmSuccess(payload, result);
    } catch (err) {
      if (isDuplicateCouponError(err)) {
        handleDuplicateCoupon();
      } else {
        toast.error(operatorErrorMessage(err));
      }
    } finally {
      confirmLock.current = false;
    }
  };

  const handleRetryAward = async () => {
    if (!lastPayload) return;
    try {
      await awardRetry.mutateAsync(lastPayload);
      applyPostConfirmSuccess(lastPayload, {
        redemptionRan: partialRedemptionRan,
        awardRan: lastPayload.awardUnits > 0,
      });
    } catch (err) {
      if (isDuplicateCouponError(err)) {
        handleDuplicateCoupon();
      } else {
        toast.error(operatorErrorMessage(err));
      }
    }
  };

  const catalogToggleNeedsProduct = Boolean(benefitState.catalog && !selectedProduct);

  // Com compra + programa cashback/pontos: não confirmar enquanto a prévia
  // ainda recalcula (resgate defaultOn liberava o botão com awardUnits stale=0).
  const previewBlocksConfirm =
    !redemptionOnly &&
    !earningBlocked &&
    !usesManualStampCount(passbook) &&
    resolvePreviewChannel(passbook) === 'bff' &&
    effectivePurchase > 0 &&
    (previewMutation.isPending || preview == null);

  const canOpenConfirm =
    Boolean(selected) &&
    !leadBlocked &&
    !redemptionExceeded &&
    !hasAddFieldErrors &&
    !previewError &&
    !catalogToggleNeedsProduct &&
    !previewBlocksConfirm &&
    (redemptionOn ||
      redemptionOnly ||
      usesManualStampCount(passbook) ||
      (preview != null &&
        (hasSaleLines || effectivePurchase > 0 || (preview.awardUnits ?? 0) > 0)));

  const showSummary = Boolean(selected) && wizardIndex >= 1;

  const accountStepIncomplete =
    (!redemptionOnly &&
      (purchaseValueFieldMeta(passbook).required || earningBlocked) &&
      parsedPurchase <= 0) ||
    (!redemptionOnly && needsProductDetails && !hasSaleLines) ||
    hasAddFieldErrors;
  const nextDisabled = leadBlocked || (wizardIndex === 1 && accountStepIncomplete);

  const onRedemptionOnlyChange = useCallback((on: boolean) => {
    setRedemptionOnly(on);
    setPreviewError(null);
    setPurchaseValue('');
    if (on) setSaleDetails(EMPTY_SALE_DETAILS);
  }, []);

  useEffect(() => {
    if (nfceKey && selected && wizardIndex === 0) {
      setWizardIndex(1);
    }
  }, [nfceKey, selected, wizardIndex]);

  const wizardSteps: WizardStep[] = useMemo(() => {
    if (!selected) return [];
    return [
      {
        id: 'cliente',
        title: 'Cliente',
        icon: UserRound,
        content: (
          <OperatorCustomerCard
            lead={selected}
            passbook={passbook}
            idPassbook={idPassbook}
            logs={recentLogs}
            logsLoading={logsLoading}
            currencyCode={currencyCode}
          />
        ),
      },
      {
        id: 'conta',
        title: 'Conta',
        icon: Receipt,
        validate: () => {
          if (leadBlocked) {
            toast.error(USER_BLOCKED_MESSAGE);
            return false;
          }
          if (
            !redemptionOnly &&
            (purchaseValueFieldMeta(passbook).required || earningBlocked) &&
            parsedPurchase <= 0
          ) {
            toast.error('Informe o valor da compra.');
            return false;
          }
          if (!redemptionOnly && needsProductDetails && !hasSaleLines) {
            toast.error('Selecione ao menos um produto.');
            return false;
          }
          if (hasAddFieldErrors) {
            const first = Object.values(addFieldErrors)[0];
            toast.error(first ?? 'Preencha os campos obrigatórios.');
            return false;
          }
          return true;
        },
        content: (
          <OperatorAccountStep
            passbook={passbook}
            purchaseValue={purchaseValue}
            onPurchaseValueChange={setPurchaseValue}
            redemptionOnly={redemptionOnly}
            onRedemptionOnlyChange={onRedemptionOnlyChange}
            redemptionOnlyDisabled={earningBlocked}
            purchaseCode={purchaseCode}
            onPurchaseCodeChange={setPurchaseCode}
            addFieldValues={addFieldValues}
            onAddFieldChange={onAddFieldChange}
            addFieldErrors={addFieldErrors}
            currencyCode={currencyCode}
            stampCount={stampCount}
            onStampCountChange={setStampCount}
            nfceKey={nfceKey}
            onScanNfce={() => setScannerOpen(true)}
            storeProducts={storeProducts}
            storeProductsLoading={storeProductsLoading}
            saleDetails={saleDetails}
            onSaleDetailChange={onSaleDetailChange}
            forceShowProducts={showProductPicker}
          />
        ),
      },
      {
        id: 'beneficios',
        title: 'Benefícios',
        icon: Gift,
        validate: () => {
          if (leadBlocked) {
            toast.error(USER_BLOCKED_MESSAGE);
            return false;
          }
          if (previewError) {
            toast.error(previewError);
            return false;
          }
          if (redemptionExceeded) {
            toast.error(redemptionError ?? 'Resgate acima do disponível.');
            return false;
          }
          if (benefitState.catalog && !selectedProduct) {
            toast.error('Selecione um produto do catálogo para resgatar.');
            return false;
          }
          if (redemptionOnly && !redemptionOn) {
            toast.error('Selecione um resgate nos benefícios.');
            return false;
          }
          if (!canOpenConfirm) {
            toast.error(
              previewError ?? 'Informe o valor da compra ou um resgate.',
            );
            return false;
          }
          return true;
        },
        content: (
          <OperatorBenefitsStep
            benefitLines={benefitLines}
            benefitState={benefitState}
            onBenefitToggle={toggleBenefit}
            redemptionAmount={redemptionAmount}
            onRedemptionAmountChange={setRedemptionAmount}
            currencyCode={currencyCode}
            program={program}
            passbook={passbook}
            balance={balance}
            catalogProducts={catalogProducts}
            catalogLoading={catalogPending}
            selectedProduct={selectedProduct}
            onSelectProduct={selectCatalogProduct}
            redemptionError={redemptionError}
          />
        ),
      },
    ];
  }, [
    selected,
    leadBlocked,
    passbook,
    idPassbook,
    recentLogs,
    logsLoading,
    currencyCode,
    purchaseValue,
    redemptionOnly,
    onRedemptionOnlyChange,
    previewError,
    purchaseCode,
    addFieldValues,
    onAddFieldChange,
    addFieldErrors,
    hasAddFieldErrors,
    parsedPurchase,
    stampCount,
    nfceKey,
    needsProductDetails,
    showProductPicker,
    hasSaleLines,
    storeProducts,
    storeProductsLoading,
    saleDetails,
    onSaleDetailChange,
    redemptionExceeded,
    redemptionError,
    redemptionOn,
    canOpenConfirm,
    benefitLines,
    benefitState,
    redemptionAmount,
    catalogProducts,
    catalogPending,
    selectedProduct,
    toggleBenefit,
    selectCatalogProduct,
    program,
    earningBlocked,
    effectivePurchase,
    redemptionValue,
    passBillDiscount,
    billDiscount,
    balance,
  ]);

  // Sheet/recibo: cupom digitado (nunca o op- de idempotência).
  const displayPurchaseCode =
    sanitizePurchaseCode(purchaseCode) ||
    sanitizePurchaseCode(addFieldValues.numberCodCupom ?? '') ||
    undefined;

  return (
    <div
      className={cn(
        'mx-auto flex w-full flex-col transition-[max-width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
        // Mobile: altura mínima estável (svh) sem max-height — teclado/resizes-content
        // não esmaga o flex. Desktop: painel travado na viewport.
        'min-h-[calc(100svh-6.5rem)] lg:h-[calc(100dvh-4rem)] lg:max-h-[calc(100dvh-4rem)] lg:min-h-0',
        selected ? 'max-w-6xl' : 'max-w-3xl',
      )}
    >
      <div className="mb-2 shrink-0 sm:mb-3">
        <PageHeader
          title="Operador"
          description={selected ? undefined : passbook.organizationName}
          help="Identifique o cliente, monte a conta e confirme. Resgate e lançamento saem juntos."
          className="mb-0 min-w-0 [&_h1]:text-xl sm:[&_h1]:text-2xl [&_p]:mt-0.5"
        />
      </div>

      <div
        className={cn(
          'grid flex-1 gap-2 sm:gap-3 lg:min-h-0',
          showSummary
            ? 'lg:grid-cols-[minmax(0,1fr)_auto] lg:grid-rows-1 lg:items-stretch'
            : selected
              ? 'lg:grid-cols-[minmax(0,1fr)_auto] lg:items-stretch'
              : '',
        )}
      >
        <div className="glass glass-sheen relative flex min-w-0 flex-col overflow-x-hidden rounded-[1.5rem] p-3 sm:p-4 lg:row-start-1 lg:min-h-0">
          <AnimatePresence mode="wait" initial={false}>
            {selected ? (
              <OperatorViewFrame viewKey={`sale-${selected._id}`}>
                <Wizard
                  key={selected._id}
                  steps={wizardSteps}
                  stepIndex={wizardIndex}
                  onStepIndexChange={setWizardIndex}
                  clickableStepper
                  mobileOptimized
                  scrollableBody
                  labels={{ cancel: 'Trocar cliente' }}
                  onCancel={clearCustomer}
                  finishLabel="Revisar e confirmar"
                  finishDisabled={!canOpenConfirm}
                  nextDisabled={nextDisabled}
                  onFinish={async () => {
                    if (leadBlocked) {
                      toast.error(USER_BLOCKED_MESSAGE);
                      return;
                    }
                    setPartialError(null);
                    setPartialRedemptionRan(false);
                    // Alinha o preview da UI com o mesmo motor do confirm
                    // (compra + resgate → ganho no líquido), antes de abrir o sheet.
                    if (
                      !redemptionOnly &&
                      !earningBlocked &&
                      resolvePreviewChannel(passbook) === 'bff' &&
                      effectivePurchase > 0
                    ) {
                      const bill = previewOperatorBill({
                        passbook,
                        purchaseValue: effectivePurchase,
                        redemptionAmount: redemptionOn ? redemptionValue : 0,
                        billDiscount: passBillDiscount ? billDiscount : undefined,
                        productRedemption: Boolean(benefitState.catalog && selectedProduct),
                        balance,
                      });
                      setPreview(
                        applyEarningBlock(
                          {
                            ...bill,
                            program: bill.program,
                            source: 'bff',
                          },
                          earningBlocked,
                        ),
                      );
                    }
                    setConfirmOpen(true);
                  }}
                  className="flex-1 gap-2 sm:gap-4 lg:min-h-0"
                />
              </OperatorViewFrame>
            ) : (
              <OperatorViewFrame viewKey="identify">
                <OperatorSearchBar
                  query={query}
                  onQueryChange={setQuery}
                  placeholder={`${passbook.fieldUniqueIdLabel ?? 'CPF'} ou nome…`}
                  loading={search.isPending || searchStatus === 'loading'}
                  status={searchStatus}
                  results={results}
                  onSelect={selectLead}
                  onScanClick={() => setScannerOpen(true)}
                  onRestartSearch={() => {
                    setQuery('');
                    setDebounced('');
                    setResults([]);
                    setSearchStatus('idle');
                    autoSelectSingleFromQr.current = false;
                  }}
                  idPassbook={idPassbook}
                  labelQtde={quantityLabel(passbook)}
                  isCashback={Boolean(passbook.hasCashback) || program === 'cashback'}
                  uniqueIdLabel={passbook.fieldUniqueIdLabel ?? 'CPF'}
                  shortUrl={passbook.shortUrl}
                  organizationName={passbook.organizationName}
                />
                {searchStatus === 'idle' ? (
                  <OperatorIdentifyEmpty
                    uniqueIdLabel={passbook.fieldUniqueIdLabel ?? 'CPF'}
                    onScan={() => setScannerOpen(true)}
                  />
                ) : null}
              </OperatorViewFrame>
            )}
          </AnimatePresence>

          <div
            className={cn(
              'mt-2 flex shrink-0 items-center justify-between gap-3 border-t border-dashed border-[hsl(var(--glass-border)/0.45)] pt-2 text-[10px] uppercase tracking-wide text-muted-foreground',
              selected && 'hidden sm:flex',
            )}
          >
            <span>Fixe CRM & Fidelidade</span>
            <span>{selected ? 'venda em andamento' : 'aguardando cliente'}</span>
          </div>
        </div>

        <AnimatePresence mode="popLayout">
          {showSummary ? (
            <OperatorSummaryCard
              key="operator-summary"
              passbook={passbook}
              preview={preview}
              previewLoading={previewMutation.isPending}
              previewError={previewError}
              redemptionExceeded={redemptionExceeded}
              currencyCode={currencyCode}
              redemptionOn={redemptionOn}
              redemptionProductName={selectedProduct?.name}
            />
          ) : null}
        </AnimatePresence>
      </div>

      <OperatorConfirmSheet
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        passbook={passbook}
        preview={preview}
        previewError={previewError}
        redemptionOn={redemptionOn}
        currencyCode={currencyCode}
        purchaseCode={displayPurchaseCode}
        redemptionProductName={selectedProduct?.name}
        redemptionRan={partialRedemptionRan}
        confirming={combo.isPending}
        partialError={partialError}
        onConfirm={handleConfirm}
        onRetryAward={handleRetryAward}
        retrying={awardRetry.isPending}
      />

      <OperatorQrScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleQr} />

      <OperatorSuccessOverlay
        open={successOpen}
        reward={successReward}
        currencyCode={currencyCode}
        onComplete={finishSuccessSequence}
      />

      <OperatorReceipt
        open={receiptOpen}
        data={receiptData}
        currencyCode={currencyCode}
        onClose={finishReceiptSequence}
      />
    </div>
  );
}

function isToday(date: string | Date | undefined): boolean {
  if (!date) return false;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}
